import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { MapPin, Maximize, BedDouble, Bath, Building2, Phone, Mail, Check, ArrowLeft, Compass, Layers, Home as HomeIcon, CalendarClock, BadgeCheck, Sofa, IndianRupee } from "lucide-react";
import api, { inr, formatApiError } from "../api";
import EMICalculator from "../components/EMICalculator";

export default function PropertyDetail() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [active, setActive] = useState(0);
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get(`/properties/${id}`).then((r) => { setP(r.data); setActive(0); }).catch(() => setNotFound(true));
  }, [id]);

  const submitLead = async (e) => {
    e.preventDefault();
    setErr("");
    try {
      await api.post("/leads", { ...form, property_id: id });
      setSent(true);
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail));
    }
  };

  if (notFound)
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 text-neutral-500">
        <p>Property not found.</p>
        <Link to="/properties" className="text-brand-600">Back to search</Link>
      </div>
    );
  if (!p) return <div className="min-h-screen flex items-center justify-center text-neutral-500">Loading…</div>;

  const gallery = (p.images && p.images.length > 0) ? p.images : [p.image].filter(Boolean);
  const input = "w-full bg-neutral-50 border border-neutral-200 rounded-xl px-4 py-3 text-neutral-900 placeholder-neutral-400 focus:outline-none focus:border-brand-500";

  const facts = [
    p.carpet_area != null && { icon: Maximize, label: "Carpet Area", value: `${p.carpet_area} sq.ft` },
    p.size_sqft != null && { icon: Maximize, label: "Built-up Area", value: `${p.size_sqft} sq.ft` },
    p.price_per_sqft != null && { icon: IndianRupee, label: "Price / sq.ft", value: `₹${Number(p.price_per_sqft).toLocaleString("en-IN")}` },
    p.bedrooms != null && { icon: BedDouble, label: "Bedrooms", value: `${p.bedrooms} BHK` },
    p.bathrooms != null && { icon: Bath, label: "Bathrooms", value: p.bathrooms },
    p.balconies != null && { icon: HomeIcon, label: "Balconies", value: p.balconies },
    p.facing && { icon: Compass, label: "Facing", value: p.facing },
    p.furnishing && { icon: Sofa, label: "Furnishing", value: p.furnishing },
    (p.floor_number != null || p.total_floors != null) && { icon: Layers, label: "Floor", value: `${p.floor_number ?? "-"}${p.total_floors ? ` of ${p.total_floors}` : ""}` },
  ].filter(Boolean);

  const overview = [
    p.builder_name && { label: "Builder / Developer", value: p.builder_name },
    p.construction_status && { label: "Status", value: p.construction_status },
    p.possession_date && { label: "Possession", value: p.possession_date },
    p.rera_id && { label: "RERA ID", value: p.rera_id },
    p.total_towers != null && { label: "Total Towers", value: p.total_towers },
    p.total_units != null && { label: "Total Units", value: p.total_units },
    p.total_floors != null && { label: "Total Floors", value: p.total_floors },
    p.bhk_config && { label: "Configurations", value: p.bhk_config },
    p.maintenance_charge != null && { label: "Maintenance", value: `₹${Number(p.maintenance_charge).toLocaleString("en-IN")}/mo` },
    p.age_of_property && { label: "Age of Property", value: p.age_of_property },
    p.available_from && { label: "Available From", value: p.available_from },
  ].filter(Boolean);

  return (
    <main className="bg-ink min-h-screen pt-32 pb-16">
      <div className="max-w-6xl mx-auto px-6">
        <Link to="/properties" className="inline-flex items-center gap-2 text-neutral-500 hover:text-neutral-900 mb-6">
          <ArrowLeft className="w-4 h-4" /> Back to results
        </Link>

        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            {/* GALLERY */}
            <div className="relative rounded-3xl overflow-hidden mb-3 shadow-card">
              <img src={gallery[active]} alt={p.title} className="w-full h-[420px] object-cover" data-testid="detail-image" />
              <span className="absolute top-4 left-4 px-4 py-1.5 text-sm font-semibold rounded-full bg-brand-500 text-white">For {p.listing_type}</span>
              {p.construction_status && (
                <span className="absolute top-4 right-4 px-4 py-1.5 text-sm font-semibold rounded-full bg-navy text-white flex items-center gap-1">
                  <BadgeCheck className="w-4 h-4" /> {p.construction_status}
                </span>
              )}
            </div>
            {gallery.length > 1 && (
              <div className="flex gap-3 mb-6 overflow-x-auto pb-1" data-testid="detail-thumbs">
                {gallery.map((g, i) => (
                  <button key={i} onClick={() => setActive(i)}
                    className={`shrink-0 rounded-xl overflow-hidden border-2 ${i === active ? "border-brand-500" : "border-transparent"}`}>
                    <img src={g} alt="" className="w-24 h-16 object-cover" />
                  </button>
                ))}
              </div>
            )}

            <div className="flex flex-wrap items-center gap-4 mb-4 mt-2">
              <span className="text-3xl font-bold text-brand-600" data-testid="detail-price">
                {inr(p.price)}{p.listing_type === "Rent" && <span className="text-base text-neutral-400 font-normal">/mo</span>}
              </span>
              <span className="px-3 py-1 rounded-full bg-white border border-neutral-200 text-sm flex items-center gap-1 text-neutral-700">
                <Building2 className="w-4 h-4 text-brand-500" /> {p.property_type}
              </span>
            </div>

            <h1 className="font-display text-3xl md:text-4xl font-bold text-navy mb-2" data-testid="detail-title">{p.title}</h1>
            <p className="flex items-center gap-2 text-neutral-500 mb-6">
              <MapPin className="w-4 h-4" /> {p.address || `${p.area}, ${p.city}`}
            </p>

            {/* KEY FACTS */}
            {facts.length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 py-6 border-y border-neutral-200 mb-6" data-testid="detail-facts">
                {facts.map((f) => (
                  <div key={f.label} className="flex items-center gap-3">
                    <f.icon className="w-5 h-5 text-brand-500 shrink-0" />
                    <div>
                      <p className="text-xs text-neutral-500">{f.label}</p>
                      <p className="font-semibold text-neutral-900">{f.value}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* OVERVIEW */}
            {overview.length > 0 && (
              <div className="mb-8">
                <h3 className="text-xl font-semibold text-navy mb-3">Project Overview</h3>
                <div className="grid sm:grid-cols-2 gap-x-8 gap-y-3 bg-surface border border-neutral-200 rounded-2xl p-6 shadow-card" data-testid="detail-overview">
                  {overview.map((o) => (
                    <div key={o.label} className="flex justify-between gap-4 border-b border-neutral-100 pb-2">
                      <span className="text-neutral-500">{o.label}</span>
                      <span className="font-medium text-neutral-900 text-right">{o.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <h3 className="text-xl font-semibold text-navy mb-3">Description</h3>
            <p className="text-neutral-600 leading-relaxed mb-8">{p.description}</p>

            {p.amenities?.length > 0 && (
              <>
                <h3 className="text-xl font-semibold text-navy mb-3">Amenities</h3>
                <div className="flex flex-wrap gap-3">
                  {p.amenities.map((a) => (
                    <span key={a} className="flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-neutral-200 text-neutral-700 text-sm">
                      <Check className="w-4 h-4 text-brand-500" /> {a}
                    </span>
                  ))}
                </div>
              </>
            )}

            {p.listing_type === "Buy" && (
              <div className="mt-10">
                <EMICalculator price={p.price} />
              </div>
            )}
          </div>

          {/* ENQUIRY */}
          <aside className="lg:col-span-1">
            <div className="sticky top-28 bg-surface border border-neutral-200 rounded-2xl p-6 shadow-card">
              <h3 className="text-xl font-semibold text-navy mb-1">Interested?</h3>
              <p className="text-neutral-500 text-sm mb-5">Send an enquiry — the lister will reach out.</p>

              <div className="space-y-2 mb-5 text-sm text-neutral-600">
                {p.contact_name && <p className="font-medium text-neutral-900">{p.contact_name}</p>}
                {p.contact_phone && <p className="flex items-center gap-2"><Phone className="w-4 h-4 text-brand-500" /> {p.contact_phone}</p>}
                {p.contact_email && <p className="flex items-center gap-2"><Mail className="w-4 h-4 text-brand-500" /> {p.contact_email}</p>}
              </div>

              {sent ? (
                <div className="text-center py-6" data-testid="lead-success">
                  <Check className="w-10 h-10 text-brand-500 mx-auto mb-2" />
                  <p className="text-neutral-900 font-medium">Enquiry sent!</p>
                  <p className="text-neutral-500 text-sm">We'll be in touch shortly.</p>
                </div>
              ) : (
                <form onSubmit={submitLead} className="space-y-3" data-testid="lead-form">
                  <input required placeholder="Your name" className={input} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="lead-name" />
                  <input required type="email" placeholder="Email" className={input} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="lead-email" />
                  <input required placeholder="Phone" className={input} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="lead-phone" />
                  <textarea placeholder="Message (optional)" rows={3} className={input} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="lead-message" />
                  {err && <p className="text-red-500 text-sm">{err}</p>}
                  <button type="submit" data-testid="lead-submit-btn"
                    className="w-full py-3 font-semibold text-white bg-gradient-to-r from-brand-500 to-brand-600 rounded-xl hover:shadow-soft transition-all active:scale-95">
                    Send Enquiry
                  </button>
                </form>
              )}
            </div>
          </aside>
        </div>
      </div>
    </main>
  );
}
